import { useEffect, useState, useCallback } from 'react';
import { Outlet, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import isEqual from 'lodash.isequal';
import { v4 as uuidv4 } from 'uuid';
import {
  PermissionDenied,
  PermissionEnum,
  Popup,
  ResponseError,
  responseErrorsResolver,
  usePrevious,
  SidePanel,
  SidePanelType,
  ButtonPrimary,
  ButtonSecondary,
  ajvResolver,
  SidePanelLoader,
} from '@ui/src';
import { useAppSelector, useAppDispatch } from '@store';
import {
  useGetPspIntegrationsQuery,
  useApplyPspPartnerIntegrationMutation,
  useAddPspPaymentsSettingsMutation,
  useUpdatePspPaymentsSettingsMutation,
  useLazyGetPspPaymentsSettingQuery,
  useLazyGetPspPartnerQuery,
  useLazyGetBankCASPQuery,
  useLazyGetCaspChainsQuery,
  useCreateCaspChainsMutation,
  useDeleteCaspChainsMutation,
  useUpdateExternalVaultSettingsMutation,
  useLazyGetPspIntegrationExternalChainsQuery,
  useUpdateExternalAccountAutoCreateCurrenciesSettingsMutation,
  useLazyGetPspIntegrationFeeTierListQuery,
  useCreatePspIntegrationFeeTierListMutation,
  useLazyGetPspIntegrationFeeTierListItemQuery,
  useUpdatePspIntegrationFeeTierListMutation,
  useDeletePspIntegrationFeeTierListMutation,
  useUpdateExchangeToleranceSettingsMutation,
} from './store/services';
import {
  ExternalAccountCurrenciesSettings,
  ExternalVaultSettings,
  FeeTierEnum,
  PspIntegration,
  TransactionFeeSettings,
  TransactionFeeSettingsFormValues,
} from './types';
import { sortCodeRegexp } from './variables/schema';
import PspIntegrations from '../../components/bank-and-psp/psp-integrations';
import { setChains as setStoreChains } from './store/slices/info';
import FeeSettingsForm from '../../components/bank-and-psp/fee-settings-form';
import useGetExternalAccounts from './helpers/use-get-external-accounts';
import { useLazyGetConfigurationDictionariesTransactionTypesQuery } from '../../store/dictionaries/service';

import cn from './bank-and-psp.module.css';

const schema = {
  type: 'object',
  properties: {
    caspId: {
      type: 'string',
      minLength: 1,
      errorMessage: 'CASP is required',
    },
    feeTier: {
      type: 'string',
      minLength: 1,
      errorMessage: 'Fee Tier is required',
    },
  },
  required: ['caspId', 'feeTier'],
};

const PSPIntegrations = () => {
  const [selectedPspIntegration, setSelectedPspIntegration] = useState<
    PspIntegration | undefined
  >(undefined);
  const [sortCodeError, setSortCodeError] = useState('');
  const [sortCode, setSortCode] = useState('');
  const [bic, setBic] = useState('');
  const [chains, setChains] = useState<
    { caspId: string; chain: string; id: string }[]
  >([]);
  const [addChainIsVisible, setAddChainIsVisible] = useState(false);
  const [deletePopupVisible, setDeletePopupVisible] = useState(false);
  const [selectedChainId, setSelectedChainId] = useState('');
  const [deleteChainError, setDeleteChainError] = useState('');
  const [externalVaultSettings, setExternalVaultSettings] =
    useState<ExternalVaultSettings>();
  const [chainCreationErrors, setChainCreationErrors] = useState<
    { id: string; error: ResponseError }[]
  >([]);
  const [configureCASPCurrenciesVisible, setConfigureCASPCurrenciesVisible] =
    useState(false);
  const [
    globalExternalVaultSettingsError,
    setGlobalExternalVaultSettingsError,
  ] = useState('');
  const [
    globalExternalCurrenciesSettingsError,
    setGlobalExternalCurrenciesSettingsError,
  ] = useState('');
  const [currenciesIsRequiredError, setCurrenciesIsRequiredError] =
    useState('');
  const [currencyCASPRequiredErrors, setCurrencyCASPRequiredErrors] = useState<
    { id: string; error: string }[]
  >([]);
  const [outgoingTransactionsFeeSettings, setOutgoingTransactionsFeeSettings] =
    useState<TransactionFeeSettings[]>([]);
  const [nostroTransactionsFeeSettings, setNostroTransactionsFeeSettings] =
    useState<TransactionFeeSettings[]>([]);
  const [selectedTransactionType, setSelectedTransactionType] = useState<
    'outgoing' | 'nostro' | undefined
  >();
  const [feeSettingsPanelIsOpen, setFeeSettingsPanelIsOpen] = useState(false);
  const [dirtyPopupVisible, setDirtyPopupVisible] = useState(false);
  const [deleteFeeSettingsPopupVisible, setDeleteFeeSettingsPopupVisible] =
    useState(false);
  const [feeTierGlobalError, setFeeTierGlobalError] = useState('');
  const [selectedFeeTierItemId, setSelectedFeeTierItemId] = useState('');
  const [deleteFeeTierError, setDeleteFeeTierError] = useState('');
  const [exchangeRateTolerance, setExchangeRateTolerance] = useState<{
    hasExchangeRateTolerance?: boolean;
    exchangeRateTolerance?: number | string | null;
  }>({
    hasExchangeRateTolerance: false,
  });
  const [globalToleranceError, setGlobalToleranceError] = useState('');

  const { bankId } = useParams();

  const prevSelectedPspIntegration = usePrevious(selectedPspIntegration);

  const dispatch = useAppDispatch();

  const {
    bankAndPsp: {
      info: {
        generalInfo,
        paymentsSettings,
        pspIntegrations,
        casp,
        chains: integrationChains,
        chainsList,
        feeTiers,
      },
    },
    configurationDictionaries: {
      activePermissions,
      paymentTypes,
      transactionTypes,
    },
    root: { currencies },
  } = useAppSelector((state) => state);

  const {
    control,
    handleSubmit,
    formState: { isDirty, errors },
    setValue,
    reset,
    setError,
  } = useForm<TransactionFeeSettingsFormValues>({
    defaultValues: {
      caspId: '',
      feeTier: FeeTierEnum.LOW,
      feeIntegrationAccountId: '',
    },
    resolver: ajvResolver(schema),
  });

  const {
    formState: { errors: toleranceErrors },
    setError: setToleranceError,
    clearErrors,
  } = useForm();

  const [getPartner, { isLoading, isFetching }] = useLazyGetPspPartnerQuery();

  const {
    isLoading: pspIntegrationsIsLoading,
    isFetching: pspIntegrationsIsFetching,
  } = useGetPspIntegrationsQuery();
  const [
    getPspPaymentsSettings,
    { isLoading: settingsIsLoading, isFetching: settingsIsFetching },
  ] = useLazyGetPspPaymentsSettingQuery();
  const [
    getBankCasp,
    { isLoading: caspIsLoading, isFetching: caspIsFetching },
  ] = useLazyGetBankCASPQuery();
  const [
    getCaspChains,
    { isLoading: caspChainsIsLoading, isFetching: caspChainsIsFetching },
  ] = useLazyGetCaspChainsQuery();
  const [deleteChain, { isLoading: deleteChainInProgress, isSuccess }] =
    useDeleteCaspChainsMutation();
  const [
    updateExternalSettings,
    { isLoading: updateExternalSettingsIsLoading },
  ] = useUpdateExternalVaultSettingsMutation();

  const [
    getChains,
    { isLoading: chainsIsLoading, isFetching: chainsIsFetching },
  ] = useLazyGetPspIntegrationExternalChainsQuery();
  const [
    getFeeTierList,
    { isLoading: feeTierIsLoading, isFetching: feeTierIsFetching },
  ] = useLazyGetPspIntegrationFeeTierListQuery();
  const [
    getFeeTierItem,
    { isLoading: feeTierItemIsLoading, isFetching: feeTierItemIsFetching },
  ] = useLazyGetPspIntegrationFeeTierListItemQuery();
  const [getTransactionTypes] =
    useLazyGetConfigurationDictionariesTransactionTypesQuery();

  const [
    updateExternalAccountAutoCreateCurrenciesSettings,
    { isLoading: updateCurrenciesSettingsInProgress },
  ] = useUpdateExternalAccountAutoCreateCurrenciesSettingsMutation();

  const [
    addPaymentsSettings,
    {
      isLoading: addPaymentSettingsIsLoading,
      isSuccess: addPaymentSettingsIsSuccess,
    },
  ] = useAddPspPaymentsSettingsMutation();

  const [
    updatePaymentSettings,
    {
      isLoading: updatePaymentSettingsInProgress,
      isSuccess: updatePaymentSettingsIsSuccess,
    },
  ] = useUpdatePspPaymentsSettingsMutation();

  const [
    applyPspIntegration,
    { isLoading: applyInProgress, isSuccess: applyIsSuccess },
  ] = useApplyPspPartnerIntegrationMutation();
  const [applyChainsMapping, { isLoading: applyChainsMappingInProgress }] =
    useCreateCaspChainsMutation();
  const [
    createFeeTier,
    { isLoading: createFeeTierInProgres, isSuccess: createFeeTierIsSuccess },
  ] = useCreatePspIntegrationFeeTierListMutation();
  const [
    updateFeeTier,
    { isLoading: updateFeeTierInProgress, isSuccess: updateFeeTierIsSuccess },
  ] = useUpdatePspIntegrationFeeTierListMutation();
  const [
    deleteFeeTier,
    { isLoading: deleteFeeTierInProgress, isSuccess: deleteFeeTierIsSuccess },
  ] = useDeletePspIntegrationFeeTierListMutation();
  const [
    updateExchangeRateTolerance,
    { isLoading: updateExcahngeRateToleranceInProgress },
  ] = useUpdateExchangeToleranceSettingsMutation();

  const {
    autocompleteList,
    pagination,
    onSearch,
    isLoading: externalAccountsIsLoading,
    onClear,
    onScroll,
    searchValue,
    nextPageIsLoading,
    getAutocompleteListAction,
  } = useGetExternalAccounts(`&pspPartnerId=${bankId}`);

  const getPartnerAction = useCallback(() => {
    getPartner(bankId as string);
  }, [bankId]);

  const onUpdateExchangeRateTolerance = async () => {
    setGlobalToleranceError('');

    if (Number(exchangeRateTolerance?.exchangeRateTolerance) <= 0) {
      setToleranceError('exchangeRateTolerance', {
        type: 'custom',
        message: 'Exchange Rate Tolerance must be 0.01 or higher',
      });
    } else {
      const response = (await updateExchangeRateTolerance({
        id: selectedPspIntegration?.id as string,
        body: {
          hasExchangeRateTolerance:
            exchangeRateTolerance.hasExchangeRateTolerance,
          exchangeRateTolerance: exchangeRateTolerance?.exchangeRateTolerance
            ? Number(exchangeRateTolerance?.exchangeRateTolerance)
            : null,
        },
      })) as {
        error?: ResponseError;
      };

      if (response?.error) {
        responseErrorsResolver({
          errors: response?.error,
          setError: setToleranceError,
          setGlobalError: setGlobalToleranceError,
        });
      }
    }
  };

  const onApplyPspIntegration = () => {
    applyPspIntegration({
      id: bankId as string,
      body: {
        pspIntegrationId: selectedPspIntegration?.id as string,
      },
    });
  };

  const onUpdateExternalVaultSettings = async (data: ExternalVaultSettings) => {
    setExternalVaultSettings(data);
  };

  const updateExternalSettinsAction = async () => {
    setGlobalExternalCurrenciesSettingsError('');
    setGlobalExternalVaultSettingsError('');
    setCurrenciesIsRequiredError('');
    setCurrencyCASPRequiredErrors([]);

    if (
      !isEqual(
        {
          externalAccountsAutoCreate:
            externalVaultSettings?.externalAccountsAutoCreate as boolean,
          externalPspPartnerAccountCreate:
            externalVaultSettings?.externalPspPartnerAccountCreate as boolean,
        },
        {
          externalAccountsAutoCreate:
            selectedPspIntegration?.externalAccountsAutoCreate,
          externalPspPartnerAccountCreate:
            selectedPspIntegration?.externalPspPartnerAccountCreate,
        },
      )
    ) {
      const response = await updateExternalSettings({
        id: selectedPspIntegration?.id as string,
        body: {
          externalAccountsAutoCreate:
            externalVaultSettings?.externalAccountsAutoCreate,
          externalPspPartnerAccountCreate:
            externalVaultSettings?.externalPspPartnerAccountCreate,
        },
      });

      if (response?.error) {
        responseErrorsResolver({
          errors: response?.error as ResponseError,
          setError: () => {},
          setGlobalError: setGlobalExternalVaultSettingsError,
        });
      } else {
        setSelectedPspIntegration({
          ...selectedPspIntegration,
          externalAccountsAutoCreate:
            externalVaultSettings?.externalAccountsAutoCreate,
          externalPspPartnerAccountCreate:
            externalVaultSettings?.externalPspPartnerAccountCreate,
        } as PspIntegration);
      }
    }

    if (
      (externalVaultSettings?.externalAccountsAutoCreate ||
        (!externalVaultSettings?.externalAccountsAutoCreate &&
          selectedPspIntegration?.externalAccountsAutoCreateCurrencies
            ?.length)) &&
      activePermissions?.includes(
        PermissionEnum.PSP_INTEGRATION_ACCOUNT_AUTO_CREATE_CURRENCIES_EDIT_ACTION,
      )
    ) {
      let requestIsAvailable = true;

      const currenciesSettingsBody: ExternalAccountCurrenciesSettings = {
        externalAccountsAutoCreateCurrencyType: externalVaultSettings
          ?.externalAccountsAutoCreateCurrencies?.length
          ? 'SPECIFIC'
          : 'ALL',
      };

      if (!externalVaultSettings?.externalAccountsAutoCreate) {
        currenciesSettingsBody.externalAccountsAutoCreateCurrencyType = null;
      }

      if (externalVaultSettings?.externalAccountsAutoCreateCurrencies?.length) {
        const currenciesList: { currencyId: string; caspId?: string }[] = [];

        externalVaultSettings?.externalAccountsAutoCreateCurrencies?.forEach(
          (item) => {
            const result: {
              currencyId: string;
              caspId?: string;
            } = {
              currencyId: item.currencyId,
            };

            if (item.caspId) {
              result.caspId = item.caspId;
            }

            currenciesList.push(result);
          },
        );

        currenciesSettingsBody.externalAccountsAutoCreateCurrencies =
          currenciesList;
      } else if (
        !externalVaultSettings?.externalAccountsAutoCreateCurrencies?.length &&
        selectedPspIntegration?.externalAccountsAutoCreateCurrencies?.length
      ) {
        currenciesSettingsBody.externalAccountsAutoCreateCurrencies = null;
      }

      if (
        configureCASPCurrenciesVisible &&
        !externalVaultSettings?.externalAccountsAutoCreateCurrencies?.length
      ) {
        requestIsAvailable = false;

        setCurrenciesIsRequiredError(
          'Select at least 1 currency and specify CASP',
        );
      }

      if (externalVaultSettings?.externalAccountsAutoCreateCurrencies?.length) {
        const cryptoCurrencies: {
          id?: string;
          caspId?: string;
          currencyId: string;
        }[] = [];

        externalVaultSettings?.externalAccountsAutoCreateCurrencies.forEach(
          (item) => {
            const currencyCurrency = currencies?.find(
              (currency) => currency.id === item.currencyId,
            );

            if (currencyCurrency && !currencyCurrency.code) {
              cryptoCurrencies.push(item);
            }
          },
        );

        if (
          cryptoCurrencies?.length &&
          cryptoCurrencies?.find((item) => !item.caspId)
        ) {
          requestIsAvailable = false;

          const nextErrors = ([] as { id: string; error: string }[]).concat(
            currencyCASPRequiredErrors,
          );

          cryptoCurrencies.forEach((item) => {
            if (!item.caspId) {
              nextErrors.push({
                id: item.id as string,
                error: 'CASP select required',
              });
            }
          });

          if (nextErrors?.length) {
            setCurrencyCASPRequiredErrors(nextErrors);
          }
        }
      }

      if (
        requestIsAvailable &&
        !isEqual(
          {
            externalAccountsAutoCreateCurrencies:
              externalVaultSettings?.externalAccountsAutoCreateCurrencies?.map(
                (item) => ({
                  currencyId: item.currencyId,
                  caspId: item.caspId,
                }),
              ),
          },
          {
            externalAccountsAutoCreateCurrencies:
              selectedPspIntegration?.externalAccountsAutoCreateCurrencies,
          },
        )
      ) {
        const currenciesSettingsReponse =
          await updateExternalAccountAutoCreateCurrenciesSettings({
            id: selectedPspIntegration?.id as string,
            body: currenciesSettingsBody,
          });

        if (currenciesSettingsReponse?.error) {
          responseErrorsResolver({
            errors: currenciesSettingsReponse?.error as ResponseError,
            setError: () => {},
            setGlobalError: setGlobalExternalCurrenciesSettingsError,
          });
        } else if (currenciesSettingsReponse?.data?.data) {
          setSelectedPspIntegration({
            ...selectedPspIntegration,
            externalAccountsAutoCreate:
              currenciesSettingsReponse?.data?.data?.externalAccountsAutoCreate,
            externalPspPartnerAccountCreate:
              currenciesSettingsReponse?.data?.data
                ?.externalPspPartnerAccountCreate,
            externalAccountsAutoCreateCurrencies:
              externalVaultSettings?.externalAccountsAutoCreateCurrencies?.map(
                (item) => ({
                  currencyId: item.currencyId,
                  caspId: item.caspId,
                }),
              ),
            externalAccountsAutoCreateCurrencyType:
              externalVaultSettings?.externalAccountsAutoCreateCurrencyType,
          } as PspIntegration);
        }
      }
    }
  };

  const onDeleteChain = async () => {
    setDeleteChainError('');

    const response = (await deleteChain(selectedChainId)) as {
      error?: ResponseError;
    };

    if (response?.error) {
      responseErrorsResolver({
        errors: response?.error,
        setError: () => {},
        setGlobalError: setDeleteChainError,
      });
    }
  };

  const onApplyCaspMapping = async () => {
    setChainCreationErrors([]);

    let nextChains = (
      [] as { caspId: string; chain: string; id: string }[]
    ).concat(chains);

    const notCreatedChains = chains?.filter((item) =>
      item.id?.includes('chain'),
    );

    const nextCreationErrors: { id: string; error: ResponseError }[] = [];

    const responses = (await Promise.all(
      notCreatedChains?.map(async (item) => {
        const response = await applyChainsMapping({
          caspId: item.caspId,
          chain: item.chain,
          pspIntegrationId: selectedPspIntegration?.id as string,
          pspPartnerId: bankId as string,
        });

        if (response) {
          if (!response.error) {
            nextChains = nextChains.map((chain) => {
              let result = { ...chain };

              if (item.id === chain.id) {
                result = response?.data?.data;
              }

              return result;
            });
          } else if (response.error) {
            notCreatedChains?.forEach((chain) => {
              if (item.id === chain.id) {
                nextCreationErrors.push({
                  id: chain.id,
                  error: response.error as ResponseError,
                });
              }
            });
          }
        }

        return response;
      }),
    )) as { data: { data: { caspId: string; chain: string; id: string } } }[];

    if (responses) {
      setChains(nextChains);
      dispatch(setStoreChains(nextChains));
    }

    if (nextCreationErrors?.length) {
      setChainCreationErrors(nextCreationErrors);
    }
  };

  const onUpdatePaymentSettings = async ({
    pspIntegrationPaymentId,
    settingsId,
  }: {
    pspIntegrationPaymentId: string;
    settingsId: string;
  }) => {
    let updateAllowed = true;

    const body: {
      pspIntegrationPaymentId: string;
      pspPartnerId: string;
      bic?: string;
      sortCode?: string;
    } = {
      pspIntegrationPaymentId,
      pspPartnerId: bankId as string,
    };

    const currentPaymentSetttings = paymentsSettings?.find(
      (item) => item.id === settingsId,
    );

    if (currentPaymentSetttings) {
      if (!bic && currentPaymentSetttings.bic) {
        body.bic = '';
      } else if (bic) {
        body.bic = bic;
      }

      if (!sortCode && currentPaymentSetttings.sortCode) {
        body.sortCode = '';
      } else if (sortCode) {
        body.sortCode = sortCode;
      }

      if (body.sortCode) {
        if (!body.sortCode.match(sortCodeRegexp)) {
          updateAllowed = false;
          setSortCodeError('Sort code is incorrect');
        } else {
          updateAllowed = true;
          setSortCodeError('Sort code is incorrect');
        }
      }

      if (updateAllowed) {
        updatePaymentSettings({
          id: bankId as string,
          settingsId,
          body,
        });
      }
    }
  };

  const handleCloseFeeSettingsForm = () => {
    setFeeSettingsPanelIsOpen(false);
    setSelectedTransactionType(undefined);
    reset({
      caspId: '',
      feeTier: FeeTierEnum.LOW,
      feeIntegrationAccountId: '',
    });
    setSelectedFeeTierItemId('');
    onClear();
  };

  const onSubmitFeesSettings = handleSubmit(async (values) => {
    setFeeTierGlobalError('');

    const body: TransactionFeeSettingsFormValues = {
      caspId: values?.caspId,
      feeTier: values.feeTier,
    };

    if (values.feeIntegrationAccountId) {
      body.feeIntegrationAccountId = values.feeIntegrationAccountId;
    }

    if (selectedTransactionType === 'outgoing') {
      const outgoingTransactionType = transactionTypes?.find(
        (item) => item.name === 'OUTGOING',
      );

      if (outgoingTransactionType) {
        body.transactionTypeId = outgoingTransactionType.id;
      }
    } else if (selectedTransactionType === 'nostro') {
      const nostroTransactionType = transactionTypes?.find(
        (item) => item.name === 'NOSTRO',
      );

      if (nostroTransactionType) {
        body.transactionTypeId = nostroTransactionType.id;
      }
    }

    let response;

    if (!selectedFeeTierItemId) {
      response = (await createFeeTier({
        id: selectedPspIntegration?.id as string,
        body,
      })) as { error?: ResponseError };
    } else {
      const updateBody: {
        feeTier: string;
        feeIntegrationAccountId?: string;
      } = {
        feeTier: values?.feeTier,
      };

      if (values.feeIntegrationAccountId) {
        updateBody.feeIntegrationAccountId = values.feeIntegrationAccountId;
      }

      response = (await updateFeeTier({
        id: selectedPspIntegration?.id as string,
        itemId: selectedFeeTierItemId,
        body: updateBody,
      })) as { error?: ResponseError };
    }

    if (response?.error) {
      responseErrorsResolver({
        errors: response?.error,
        setError,
        setGlobalError: setFeeTierGlobalError,
      });
    }
  });

  const getFeeTierItemAction = async (id: string) => {
    setSelectedFeeTierItemId(id);

    const response = (await getFeeTierItem({
      id: selectedPspIntegration?.id as string,
      itemId: id,
    })) as { data: { data: TransactionFeeSettings } };

    if (response?.data?.data) {
      reset({
        caspId: response?.data?.data?.caspId,
        feeTier: response?.data?.data?.feeTier as FeeTierEnum,
        feeIntegrationAccountId:
          response?.data?.data?.feeIntegrationAccountId || '',
      });
    }
  };

  const onDeleteFeeSetting = async () => {
    setDeleteFeeTierError('');

    const response = (await deleteFeeTier({
      id: selectedPspIntegration?.id as string,
      itemId: selectedFeeTierItemId,
    })) as { error?: ResponseError };

    if (response?.error) {
      responseErrorsResolver({
        errors: response?.error,
        setError: () => {},
        setGlobalError: setDeleteFeeTierError,
      });
    }
  };

  const onAddPaymentSettings = async (pspIntegrationPaymentId: string) => {
    const body: {
      pspIntegrationPaymentId: string;
      bic?: string;
      sortCode?: string;
    } = {
      pspIntegrationPaymentId,
    };

    let createAllowed = true;

    if (bic) {
      body.bic = bic;
    }

    if (sortCode) {
      body.sortCode = sortCode;
    }

    if (body.sortCode) {
      if (!body.sortCode.match(sortCodeRegexp)) {
        setSortCodeError('Sort code is incorrect');

        createAllowed = false;
      } else {
        setSortCodeError('');

        createAllowed = true;
      }
    }

    if (createAllowed) {
      await addPaymentsSettings({
        id: bankId as string,
        body,
      });
    }
  };

  useEffect(() => {
    if (isSuccess) {
      const nextChains = chains?.filter((item) => item.id !== selectedChainId);

      setChains(nextChains);
      dispatch(setStoreChains(nextChains));

      setDeleteChainError('');
      setDeletePopupVisible(false);
      setSelectedChainId('');

      if (nextChains?.length === 0) {
        setAddChainIsVisible(false);
      }
    }
  }, [isSuccess]);

  useEffect(() => {
    if (integrationChains) {
      setChains(integrationChains);

      if (integrationChains?.length) {
        setAddChainIsVisible(true);
      }
    }
  }, [integrationChains]);

  useEffect(() => {
    if (
      generalInfo &&
      generalInfo.pspIntegration &&
      generalInfo.pspIntegrationId &&
      pspIntegrations &&
      !selectedPspIntegration
    ) {
      const currentIntegration = pspIntegrations?.find(
        (item) => item.id === generalInfo?.pspIntegrationId,
      );

      setSelectedPspIntegration(currentIntegration);
    }
  }, [generalInfo, pspIntegrations]);

  useEffect(() => {
    if (
      bankId &&
      activePermissions?.includes(PermissionEnum.PSP_LIST) &&
      !externalVaultSettings
    ) {
      if (!generalInfo) {
        getPartnerAction();
      }

      getPspPaymentsSettings(bankId);
    }
  }, [bankId, generalInfo, externalVaultSettings]);

  useEffect(() => {
    if (!casp) {
      getBankCasp();
    }
  }, [casp]);

  useEffect(() => {
    if (deleteFeeTierIsSuccess) {
      setSelectedFeeTierItemId('');
      setDeleteFeeTierError('');
      setDeleteFeeSettingsPopupVisible(false);
    }
  }, [deleteFeeTierIsSuccess]);

  useEffect(() => {
    if (createFeeTierIsSuccess || updateFeeTierIsSuccess) {
      handleCloseFeeSettingsForm();
    }
  }, [createFeeTierIsSuccess, updateFeeTierIsSuccess]);

  useEffect(() => {
    if (selectedPspIntegration) {
      if (
        selectedPspIntegration?.name === 'PRIME_VAULT_WALLET' ||
        selectedPspIntegration?.name === 'OPEN_PAYD'
      ) {
        setExternalVaultSettings({
          externalAccountsAutoCreate:
            selectedPspIntegration?.externalAccountsAutoCreate as boolean,
          externalPspPartnerAccountCreate:
            selectedPspIntegration?.externalPspPartnerAccountCreate as boolean,
          externalAccountsAutoCreateCurrencies:
            selectedPspIntegration?.externalAccountsAutoCreateCurrencies?.map(
              (item) => ({
                ...item,
                id: uuidv4(),
              }),
            ),
          externalAccountsAutoCreateCurrencyType:
            selectedPspIntegration?.externalAccountsAutoCreateCurrencyType,
        });

        if (
          selectedPspIntegration?.externalAccountsAutoCreateCurrencies?.length >
          0
        ) {
          setConfigureCASPCurrenciesVisible(true);
        }
      }

      if (
        selectedPspIntegration?.name === 'PRIME_VAULT_WALLET' &&
        activePermissions?.includes(
          PermissionEnum.PSP_INTEGRATION_FEE_TIER_LIST,
        )
      ) {
        getFeeTierList(selectedPspIntegration.id);

        if (!transactionTypes) {
          getTransactionTypes();
        }
      }
    }
  }, [selectedPspIntegration, activePermissions, transactionTypes]);

  useEffect(() => {
    if (transactionTypes && feeTiers) {
      const outgoingTransactionType = transactionTypes?.find(
        (item) => item.name === 'OUTGOING',
      );
      const nostroTransactionType = transactionTypes?.find(
        (item) => item.name === 'NOSTRO',
      );

      if (outgoingTransactionType && nostroTransactionType) {
        const outgoingFeeTiers = feeTiers?.filter(
          (item) => item.transactionTypeId === outgoingTransactionType.id,
        );
        const nostroFeeTiers = feeTiers?.filter(
          (item) => item.transactionTypeId === nostroTransactionType.id,
        );

        setOutgoingTransactionsFeeSettings(outgoingFeeTiers);
        setNostroTransactionsFeeSettings(nostroFeeTiers);
      }
    }
  }, [transactionTypes, feeTiers]);

  useEffect(() => {
    if (
      ((selectedPspIntegration && !prevSelectedPspIntegration) ||
        (selectedPspIntegration &&
          prevSelectedPspIntegration &&
          selectedPspIntegration.id !== prevSelectedPspIntegration.id)) &&
      activePermissions?.includes(
        PermissionEnum.PSP_INTEGRATION_CASPS_CHAINS_LIST,
      ) &&
      (selectedPspIntegration?.name === 'B2C2' ||
        selectedPspIntegration?.name === 'PRIME_VAULT_WALLET')
    ) {
      getCaspChains(`?all=true&pspIntegrationId=${selectedPspIntegration?.id}`);
      getChains(selectedPspIntegration.id);
    }
  }, [selectedPspIntegration, activePermissions, prevSelectedPspIntegration]);

  useEffect(() => {
    if (selectedPspIntegration) {
      setExchangeRateTolerance({
        hasExchangeRateTolerance:
          selectedPspIntegration?.hasExchangeRateTolerance,
        exchangeRateTolerance: selectedPspIntegration?.exchangeRateTolerance,
      });
    }
  }, [selectedPspIntegration]);

  let content;

  if (!activePermissions?.includes(PermissionEnum.PSP_LIST)) {
    content = <PermissionDenied />;
  } else {
    content = (
      <div className={cn.contentWrapper}>
        <div className={cn.integrationTypeTitle}>
          <span className={cn.integrationTypeTitleText}>
            {`${generalInfo?.companyName} - Integration type`}
          </span>
        </div>
        <PspIntegrations
          isLoading={
            pspIntegrationsIsLoading ||
            pspIntegrationsIsFetching ||
            isLoading ||
            isFetching ||
            settingsIsLoading ||
            settingsIsFetching ||
            caspIsLoading ||
            caspIsFetching
          }
          pspIntegrations={pspIntegrations}
          selectedPspIntegration={selectedPspIntegration}
          setSelectedPspIntegration={setSelectedPspIntegration}
          integrated={Boolean(generalInfo?.pspIntegrationId)}
          applyInProgress={applyInProgress}
          onApplyPspIntegration={onApplyPspIntegration}
          applyIsSuccess={applyIsSuccess}
          sortCode={sortCode}
          setSortCode={setSortCode}
          bic={bic}
          setBic={setBic}
          onAddPaymentSettings={onAddPaymentSettings}
          addPaymentSettingsIsLoading={addPaymentSettingsIsLoading}
          paymentSettingsSuccess={
            addPaymentSettingsIsSuccess || updatePaymentSettingsIsSuccess
          }
          paymentsSettings={paymentsSettings}
          onUpdatePaymentSettings={onUpdatePaymentSettings}
          updatePaymentSettingsInProgress={updatePaymentSettingsInProgress}
          sortCodeError={sortCodeError}
          setSortCodeError={setSortCodeError}
          isEditable={activePermissions?.includes(
            PermissionEnum.PSP_EDIT_ACTION,
          )}
          paymentTypes={paymentTypes}
          casp={casp?.map((item) => ({ name: item.name, value: item.id }))}
          caspChainsIsLoading={
            caspChainsIsLoading ||
            caspChainsIsFetching ||
            chainsIsLoading ||
            chainsIsFetching
          }
          chains={chains}
          setChains={setChains}
          applyChainsMappingInProgress={applyChainsMappingInProgress}
          onApplyCaspMapping={onApplyCaspMapping}
          addChainIsVisible={addChainIsVisible}
          setAddChainIsVisible={setAddChainIsVisible}
          setDeletePopupVisible={setDeletePopupVisible}
          setSelectedChainId={setSelectedChainId}
          externalVaultSettings={externalVaultSettings}
          onUpdateExternalVaultSettings={onUpdateExternalVaultSettings}
          updateExternalSettingsIsLoading={
            updateExternalSettingsIsLoading ||
            updateCurrenciesSettingsInProgress
          }
          bankId={bankId}
          updateExternalSettinsAction={updateExternalSettinsAction}
          chainCreationErrors={chainCreationErrors}
          setChainCreationErrors={setChainCreationErrors}
          chainsList={chainsList}
          configureCASPCurrenciesVisible={configureCASPCurrenciesVisible}
          setConfigureCASPCurrenciesVisible={setConfigureCASPCurrenciesVisible}
          globalExternalVaultSettingsError={globalExternalVaultSettingsError}
          globalExternalCurrenciesSettingsError={
            globalExternalCurrenciesSettingsError
          }
          currencies={currencies}
          currenciesIsRequiredError={currenciesIsRequiredError}
          setCurrenciesIsRequiredError={setCurrenciesIsRequiredError}
          currencyCASPRequiredErrors={currencyCASPRequiredErrors}
          setCurrencyCASPRequiredErrors={setCurrencyCASPRequiredErrors}
          setGlobalExternalCurrenciesSettingsError={
            setGlobalExternalCurrenciesSettingsError
          }
          setGlobalExternalVaultSettingsError={
            setGlobalExternalVaultSettingsError
          }
          outgoingTransactionsFeeSettings={outgoingTransactionsFeeSettings}
          nostroTransactionsFeeSettings={nostroTransactionsFeeSettings}
          setFeeSettingsPanelIsOpen={setFeeSettingsPanelIsOpen}
          setSelectedTransactionType={setSelectedTransactionType}
          setDeleteFeeSettingsPopupVisible={setDeleteFeeSettingsPopupVisible}
          feeSettingsIsLoading={feeTierIsLoading || feeTierIsFetching}
          getFeeTierItemAction={getFeeTierItemAction}
          setSelectedFeeTierItemId={setSelectedFeeTierItemId}
          exchangeRateTolerance={exchangeRateTolerance}
          setExchangeRateTolerance={setExchangeRateTolerance}
          updateExcahngeRateToleranceInProgress={
            updateExcahngeRateToleranceInProgress
          }
          onUpdateExchangeRateTolerance={onUpdateExchangeRateTolerance}
          globalToleranceError={globalToleranceError}
          toleranceErrors={toleranceErrors}
          clearErrors={clearErrors}
          setToleranceError={setToleranceError}
        />
        <Popup
          title="Delete CASP Mapping?"
          description="This mapping is not currently used by any accounts, but you will need to re-configure it if you want to use this protocol in the future."
          popupClassName={cn.deleteChainPopup}
          isVisible={deletePopupVisible}
          onCancelClick={() => {
            setDeletePopupVisible(false);
            setSelectedChainId('');
            setDeleteChainError('');
          }}
          okButtonText={deleteChainInProgress ? 'Loading...' : 'Delete'}
          cancelButtonText="Cancel"
          okButtonDisabled={deleteChainInProgress}
          onOkClick={onDeleteChain}
        >
          <div>
            {deleteChainError && (
              <div className={cn.deleteChainError}>{deleteChainError}</div>
            )}
          </div>
        </Popup>
        <Popup
          title="Delete Fee Setting?"
          description="Are you sure you want to delete this fee setting?"
          popupClassName={cn.deleteChainPopup}
          isVisible={deleteFeeSettingsPopupVisible}
          onCancelClick={() => {
            setDeleteFeeSettingsPopupVisible(false);
            setSelectedFeeTierItemId('');
            setDeleteFeeTierError('');
          }}
          okButtonText={deleteFeeTierInProgress ? 'Loading...' : 'Delete'}
          cancelButtonText="Cancel"
          onOkClick={onDeleteFeeSetting}
          okButtonDisabled={deleteFeeTierInProgress}
        >
          <div>
            {deleteFeeTierError && (
              <div className={cn.deleteChainError}>{deleteFeeTierError}</div>
            )}
          </div>
        </Popup>
        <SidePanel
          isOpen={feeSettingsPanelIsOpen}
          onClose={() => {
            if (isDirty) {
              setDirtyPopupVisible(true);
            } else {
              handleCloseFeeSettingsForm();
            }
          }}
          title={`${selectedFeeTierItemId ? 'Edit' : 'Add'} Fee Settings for ${selectedTransactionType === 'nostro' ? 'Nostro' : 'Outgoing'} Transactions`}
          sidepanelExpandedKey="fee-settings-panel"
          type={SidePanelType.Accounts}
          errorSidepanel={feeTierGlobalError}
          footer={
            <>
              <ButtonPrimary
                text={
                  createFeeTierInProgres || updateFeeTierInProgress
                    ? 'Loading...'
                    : `${selectedFeeTierItemId ? 'Edit' : 'Add'} Fee settings`
                }
                disabled={
                  !isDirty || createFeeTierInProgres || updateFeeTierInProgress
                }
                onClick={onSubmitFeesSettings}
                bold
              />
              <ButtonSecondary
                text="Close"
                onClick={() => {
                  if (isDirty) {
                    setDirtyPopupVisible(true);
                  } else {
                    handleCloseFeeSettingsForm();
                  }
                }}
                className="button-white"
                bold
              />
            </>
          }
          closeOnClickOutside
        >
          <>
            {(feeTierItemIsLoading || feeTierItemIsFetching) && (
              <SidePanelLoader />
            )}
            {!feeTierItemIsLoading && !feeTierItemIsFetching && (
              <FeeSettingsForm
                control={control}
                setValue={setValue}
                casp={casp}
                errors={errors}
                autocompleteList={autocompleteList}
                pagination={pagination}
                onSearch={onSearch}
                isLoading={externalAccountsIsLoading}
                onClear={onClear}
                onScroll={onScroll}
                searchValue={searchValue}
                nextPageIsLoading={nextPageIsLoading}
                getAutocompleteListAction={getAutocompleteListAction}
                panelIsOpen={feeSettingsPanelIsOpen}
                selectedFeeTierItemId={selectedFeeTierItemId}
              />
            )}
          </>
        </SidePanel>
        <Popup
          title="You have unsaved changes"
          description=""
          onCancelClick={() => setDirtyPopupVisible(false)}
          onOkClick={() => {
            setDirtyPopupVisible(false);
            handleCloseFeeSettingsForm();
          }}
          cancelButtonText="Cancel"
          okButtonText="Close"
          isVisible={dirtyPopupVisible}
          parent={document.getElementById('dirty-popup') as HTMLElement}
        />
        <div id="dirty-popup" />
        <Outlet />
      </div>
    );
  }

  return content;
};

export default PSPIntegrations;
